/**
 * @file This file contains configuration options related to game settings,
 * such as available grid sizes.
 * @module gameOptionsConfig
 */

/**
 * @typedef {Object} GridSizeOption
 * @property {number} key - The numerical key representing the grid size (e.g., 5 for 5x5).
 * @property {string} name - The display name for the grid size (e.g., 'Tiny', 'Medium').
 */

/**
 * Configuration object for various game options.
 * @type {Object}
 * @property {GridSizeOption[]} gridSizes - An array of available grid size options.
 * @property {number} defaultGridSizeKey - The default grid size key to be used when starting a new game.
 * @property {number} defaultTurnLimit - The default number of turns for the limited turns game mode.
 * @property {boolean} isLimitedTurnsModeEnabledByDefault - The default state for enabling limited turns mode.
 * @property {boolean} isAnimalsEnabledByDefault - The default state for enabling animals.
 * @property {boolean} isSettlersModeEnabledByDefault - The default state for enabling Settlers Mode.
 * @property {number} settlersModeCoverage - The percentage of the board to fill with settlements in Settlers Mode.
 */
export const gameOptionsConfig = {
    gridSizes: [
        { key: 5, name: 'Tiny' },
        { key: 6, name: 'Small' },
        { key: 11, name: 'Medium' },
        { key: 16, name: 'Large' },
        { key: 21, name: 'Huge' }
    ],
    defaultGridSizeKey: 5,
    defaultTurnLimit: 100, // The default number of turns for the limited turns game mode
    isLimitedTurnsModeEnabledByDefault: false, // Whether limited turns mode is enabled by default
    isAnimalsEnabledByDefault: true, // Whether animals are enabled by default
    isSettlersModeEnabledByDefault: false, // Whether Settlers Mode is enabled by default
    settlersModeCoverage: 0.3, // 30% of the playable board
    rollingGridPrefillChance: 0.7 // 70% chance for a cell to be pre-filled in Rolling Grid mode
};